import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  forwardRef,
  inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  SelectTemplateDirective,
  createSelectOverlay,
  resolveField,
  selectListId,
} from '../select/select-options';

/**
 * A search field with suggestions: after `delay` ms and at least `minLength`
 * characters it asks for suggestions (`complete`), the caller sets them, and
 * the list below the field shows them, the first one highlighted with
 * autoHighlight. While typing the model holds the text, after choosing the
 * suggestion; the field shows its `field`. A template
 * `appSelectTemplate="item"` renders the suggestions.
 *
 * With `dropdown` it is a combobox with a button: the button, ArrowDown or
 * focusing the empty field open the list without typing, and every text, also
 * a shorter one than `minLength` or none, asks for suggestions, so the caller
 * lists the first candidates for '' and filters them while the user types.
 * `hint` shows below the list.
 *
 * Paged suggestions: while `hasMore` is set, a loading row ends the list, and
 * scrolling near the end (or moving the highlight there with the keys) emits
 * `loadMore` once; the caller appends the next page to `suggestions` (a new
 * array) or clears `hasMore`. A new text resets the paging on the caller's
 * side, it asks again with `complete`.
 */
@Component({
  selector: 'app-autocomplete',
  standalone: true,
  imports: [NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteComponent),
      multi: true,
    },
  ],
  template: `<span
      #origin
      class="app-autocomplete {{ styleClass }}"
      [class.app-autocomplete-clearable]="showClear"
      [class.app-autocomplete-dd]="dropdown"
      [class.app-autocomplete-open]="open"
    >
      <input
        #input
        type="text"
        autocomplete="off"
        class="app-input app-autocomplete-input"
        role="combobox"
        aria-autocomplete="list"
        [attr.aria-expanded]="open"
        [attr.aria-controls]="open ? listId : null"
        [attr.aria-activedescendant]="activeId"
        [attr.aria-describedby]="open && hint ? listId + '-hint' : null"
        [attr.aria-label]="ariaLabel || placeholder"
        [attr.placeholder]="placeholder"
        [attr.cdkFocusInitial]="autofocus ? '' : null"
        [disabled]="disabled"
        (input)="onInput()"
        (keydown)="onKeydown($event)"
        (focus)="onFocus()"
        (blur)="onTouched()"
      />
      @if (showClear && filled && !disabled) {
        <i
          class="app-autocomplete-clear-icon app-icon fa-solid fa-xmark"
          aria-hidden="true"
          (click)="clear()"
        ></i>
      }
      @if (dropdown) {
        <!-- Not in the tab order: the input is the combobox (ARIA APG). -->
        <button
          type="button"
          class="app-autocomplete-trigger"
          tabindex="-1"
          [attr.aria-label]="dropdownAriaLabel"
          [attr.aria-expanded]="open"
          [attr.aria-controls]="open ? listId : null"
          [disabled]="disabled"
          (mousedown)="$event.preventDefault()"
          (click)="toggle()"
        >
          <i
            class="app-autocomplete-trigger-icon app-icon fa-solid fa-chevron-down"
            aria-hidden="true"
          ></i>
        </button>
      }
      @if (loading) {
        <i
          class="app-autocomplete-loader app-icon fa-solid fa-spinner fa-spin"
          aria-hidden="true"
        ></i>
      }
    </span>
    <ng-template #panel>
      <div
        class="app-autocomplete-panel {{ panelStyleClass }}"
        [style.max-height]="scrollHeight"
        (scroll)="onPanelScroll($event)"
      >
        <ul class="app-autocomplete-items" role="listbox" [id]="listId">
          @for (option of suggestions; track $index) {
            <!-- The input keeps the focus and moves through the options (aria-activedescendant). -->
            <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
            <li
              class="app-autocomplete-item"
              role="option"
              [id]="listId + '-' + $index"
              [class.app-highlight]="$index === activeIndex"
              [attr.aria-selected]="$index === activeIndex"
              (mousedown)="$event.preventDefault()"
              (click)="choose(option)"
            >
              @if (itemTemplate) {
                <ng-container
                  *ngTemplateOutlet="
                    itemTemplate;
                    context: { $implicit: option, index: $index }
                  "
                ></ng-container>
              } @else {
                <span>{{ labelOf(option) }}</span>
              }
            </li>
          }
          @if (hasMore && suggestions.length) {
            <li class="app-autocomplete-more" role="presentation">
              <i
                class="app-icon fa-solid fa-spinner fa-spin"
                aria-hidden="true"
              ></i>
              <span>{{ loadingMoreLabel }}</span>
            </li>
          }
          @if (!suggestions.length) {
            <li class="app-autocomplete-empty-message">{{ emptyMessage }}</li>
          }
        </ul>
        @if (hint) {
          <div class="app-autocomplete-hint" [id]="listId + '-hint'">
            {{ hint }}
          </div>
        }
      </div>
    </ng-template>`,
})
export class AutocompleteComponent implements ControlValueAccessor, OnDestroy {
  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);
  private cdr = inject(ChangeDetectorRef);

  /** The field of the suggestions the list and the input show. */
  @Input() field?: string;
  @Input() minLength = 1;
  @Input() delay = 300;
  @Input() placeholder?: string;
  @Input() showEmptyMessage = false;
  @Input() emptyMessage = 'No results found';
  @Input() autoHighlight = false;
  @Input() showClear = false;
  /** Marks the input for app-overlay-panel, which focuses it on opening. */
  @Input() autofocus = false;
  @Input() scrollHeight = '200px';
  @Input() styleClass = '';
  @Input() panelStyleClass = '';
  @Input() ariaLabel?: string;
  @Input() disabled = false;
  /** A button opens the list without typing; see the class comment. */
  @Input() dropdown = false;
  @Input() dropdownAriaLabel = 'Show suggestions';
  /** A line below the suggestions. */
  @Input() hint?: string | null;
  /** Another page of suggestions follows; see the class comment. */
  @Input() set hasMore(hasMore: boolean | null | undefined) {
    this._hasMore = !!hasMore;
    if (!this._hasMore) {
      this.loadingMore = false;
    }
  }
  get hasMore(): boolean {
    return this._hasMore;
  }
  private _hasMore = false;
  @Input() loadingMoreLabel = 'Loading more…';

  /** The suggestions for the last `complete` query. */
  @Input() set suggestions(suggestions: unknown[] | null | undefined) {
    this._suggestions = suggestions || [];
    this.suggestionsChanged();
  }
  get suggestions(): unknown[] {
    return this._suggestions;
  }
  private _suggestions: unknown[] = [];

  /** The text to search for. */
  @Output() complete = new EventEmitter<string>();
  /** Scrolling reached the end of the list while `hasMore` is set. */
  @Output() loadMore = new EventEmitter<void>();
  /** The suggestion a user chose. */
  @Output() selected = new EventEmitter<unknown>();
  /** The clear icon emptied the field. */
  @Output() cleared = new EventEmitter<void>();

  @ViewChild('origin', { static: true })
  private origin!: ElementRef<HTMLElement>;
  @ViewChild('input', { static: true })
  private input!: ElementRef<HTMLInputElement>;
  @ViewChild('panel', { static: true })
  private panelTemplate!: TemplateRef<unknown>;
  @ContentChildren(SelectTemplateDirective)
  private templates?: QueryList<SelectTemplateDirective>;

  readonly listId = selectListId();
  value: unknown = null;
  loading = false;
  /** `loadMore` was emitted and the next page has not arrived yet. */
  loadingMore = false;
  activeIndex = -1;

  private overlayRef: OverlayRef | null = null;
  private timeout?: ReturnType<typeof setTimeout>;
  private moreCheck?: ReturnType<typeof setTimeout>;
  private onChange: (value: unknown) => void = () => undefined;
  onTouched: () => void = () => undefined;

  get open() {
    return !!this.overlayRef?.hasAttached();
  }

  get filled() {
    return !!this.input?.nativeElement.value;
  }

  get activeId() {
    return this.open && this.activeIndex >= 0
      ? `${this.listId}-${this.activeIndex}`
      : null;
  }

  get itemTemplate() {
    return this.templates?.find((t) => t.name === 'item')?.template ?? null;
  }

  ngOnDestroy(): void {
    clearTimeout(this.timeout);
    clearTimeout(this.moreCheck);
    const ref = this.overlayRef;
    this.overlayRef = null;
    ref?.dispose();
  }

  labelOf(option: unknown) {
    const label =
      this.field && option !== null && typeof option === 'object'
        ? resolveField(option, this.field)
        : option;
    return label === null || label === undefined ? '' : String(label);
  }

  writeValue(value: unknown): void {
    this.value = value;
    this.input.nativeElement.value = this.labelOf(value);
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.cdr.markForCheck();
  }

  onInput() {
    clearTimeout(this.timeout);
    const text = this.input.nativeElement.value;
    this.setValue(text);
    if (this.dropdown || text.length >= this.minLength) {
      this.timeout = setTimeout(() => this.search(text), this.delay);
    } else {
      this.hide();
    }
  }

  /** The empty field of a dropdown opens the list. */
  onFocus() {
    if (this.dropdown && !this.filled && !this.open && !this.loading) {
      this.openList();
    }
  }

  /** The button: opens the list, or closes it. */
  toggle() {
    if (this.disabled) {
      return;
    }
    this.input.nativeElement.focus();
    if (this.open) {
      this.hide();
    } else if (!this.loading) {
      this.openList();
    }
  }

  onKeydown(event: KeyboardEvent) {
    if (!this.open) {
      if (
        this.dropdown &&
        event.key === 'ArrowDown' &&
        !this.loading &&
        !this.disabled
      ) {
        event.preventDefault();
        this.openList();
      }
      return;
    }
    const count = this.suggestions.length;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault();
        if (count) {
          const step = event.key === 'ArrowDown' ? 1 : -1;
          this.activeIndex = Math.max(
            0,
            Math.min(count - 1, this.activeIndex + step)
          );
          this.cdr.detectChanges();
          this.overlayRef?.overlayElement
            .querySelector(`#${this.activeId}`)
            ?.scrollIntoView?.({ block: 'nearest' });
          if (this.activeIndex >= count - 3) {
            this.requestMore();
          }
        }
        break;
      case 'Enter':
        if (this.activeIndex >= 0 && this.activeIndex < count) {
          event.preventDefault();
          this.choose(this.suggestions[this.activeIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.hide();
        break;
      case 'Tab':
        if (this.activeIndex >= 0 && this.activeIndex < count) {
          this.choose(this.suggestions[this.activeIndex]);
        }
        this.hide();
        break;
    }
  }

  choose(option: unknown) {
    this.input.nativeElement.value = this.labelOf(option);
    this.setValue(option);
    this.selected.emit(option);
    this.hide();
    this.input.nativeElement.focus();
  }

  clear() {
    clearTimeout(this.timeout);
    this.input.nativeElement.value = '';
    this.setValue(null);
    this.hide();
    this.cleared.emit();
  }

  /** Near the end of the scrolled list the next page loads. */
  onPanelScroll(event: Event) {
    if (nearEnd(event.target as HTMLElement)) {
      this.requestMore();
    }
  }

  hide() {
    clearTimeout(this.moreCheck);
    this.loadingMore = false;
    const ref = this.overlayRef;
    this.overlayRef = null;
    ref?.dispose();
    this.activeIndex = -1;
    this.cdr.markForCheck();
  }

  /**
   * Asks for the suggestions of the typed text, or for all ('') while the
   * field shows a chosen suggestion or nothing.
   */
  private openList() {
    clearTimeout(this.timeout);
    this.search(typeof this.value === 'string' ? this.value : '');
  }

  private search(text: string) {
    this.loading = true;
    this.loadingMore = false;
    this.cdr.markForCheck();
    this.complete.emit(text);
  }

  private requestMore() {
    if (!this.hasMore || this.loadingMore || this.loading || !this.open) {
      return;
    }
    this.loadingMore = true;
    this.cdr.markForCheck();
    this.loadMore.emit();
  }

  /**
   * A page that does not fill the list leaves nothing to scroll: the next one
   * loads at once, after the list has rendered.
   */
  private checkMoreLater() {
    clearTimeout(this.moreCheck);
    if (!this.hasMore) {
      return;
    }
    this.moreCheck = setTimeout(() => {
      const panel = this.overlayRef?.overlayElement.querySelector(
        '.app-autocomplete-panel'
      ) as HTMLElement | null;
      if (panel && nearEnd(panel)) {
        this.requestMore();
      }
    });
  }

  // Only the answer to a search, or to loadMore, changes the open list.
  private suggestionsChanged() {
    if (this.loadingMore) {
      this.loadingMore = false;
      this.activeIndex = Math.min(
        this.activeIndex,
        this._suggestions.length - 1
      );
      this.overlayRef?.updatePosition();
      this.checkMoreLater();
      this.cdr.markForCheck();
      return;
    }
    if (!this.loading) {
      return;
    }
    this.loading = false;
    this.activeIndex = this.autoHighlight && this._suggestions.length ? 0 : -1;
    if (this._suggestions.length || this.showEmptyMessage) {
      this.show();
      this.checkMoreLater();
    } else {
      this.hide();
    }
    this.cdr.markForCheck();
  }

  private show() {
    if (this.open) {
      this.overlayRef?.updatePosition();
      return;
    }
    const origin = this.origin.nativeElement;
    const ref = createSelectOverlay(this.overlay, origin);
    this.overlayRef = ref;
    ref.attach(new TemplatePortal(this.panelTemplate, this.viewContainerRef));
    ref.outsidePointerEvents().subscribe((event) => {
      if (!origin.contains(event.target as Node)) {
        this.hide();
      }
    });
    ref.detachments().subscribe(() => {
      if (this.overlayRef === ref) {
        this.hide();
      }
    });
  }

  private setValue(value: unknown) {
    this.value = value;
    this.onChange(value);
    this.cdr.markForCheck();
  }
}

/** Within 48px of the end of a list that has rendered (jsdom has no layout). */
const nearEnd = (element: HTMLElement | null) =>
  !!element &&
  element.scrollHeight > 0 &&
  element.scrollTop + element.clientHeight >= element.scrollHeight - 48;
